import React, { forwardRef, memo } from 'react';

import type { AvatarProps } from './types';

const VIEW_BOX = '0 0 366.34 366.34';

type AvatarSvgProps = AvatarProps & { children: React.ReactNode };

/**
 * Base SVG wrapper for avatar icons with consistent viewBox and size
 */
const AvatarSvg = memo(
  forwardRef<SVGSVGElement, AvatarSvgProps>(({ size = 60, className, children, ...props }, ref) => (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={VIEW_BOX}
      width={size}
      height={size}
      className={className}
      {...props}
    >
      {children}
    </svg>
  )),
);
AvatarSvg.displayName = 'AvatarSvg';

/**
 * Alex avatar - inline SVG component
 */
export const AlexAvatar = memo((props: AvatarProps) => {
  const { size = 60, className, ref: _ref, ...rest } = props;
  return (
    <AvatarSvg size={size} className={className} {...rest}>
      <path
        fill="#daaea8"
        d="M118.72,166.8c-1.57-3.12-3.09-6.28-4.55-9.45-2.86-6.2-5.54-12.55-6.74-19.27-3.89-21.68,7.32-45.32,23.41-59.22,22.9-19.77,55.34-27.24,80.57-44.78a34.59,34.59,0,0,1-12.9,21.76c11.81-9.1,47,5.89,58,11.51a116.3,116.3,0,0,1,44.94,40.08,73.87,73.87,0,0,0-37.51-4.8c9.88,9.57,20.16,19.78,23.9,33C270.11,122.85,241.8,129,231,148.05c-6.93,12.2-6.83,27.06-11,40.47-4.4,14.2-16.27,23.22-30.23,27-10.52,2.88-31.22,4.18-40.75-2-2.89-1.87-5-4.73-6.94-7.55A318.87,318.87,0,0,1,118.72,166.8Z"
      />
      <circle fill="#845161" cx="129.75" cy="170.77" r="17" />
      <circle fill="#69303a" cx="135.61" cy="169.77" r="16" />
      <path
        fill="#845161"
        d="M291.34,294a184.56,184.56,0,0,1-226.48-1l48.66-22.81a46.83,46.83,0,0,0,6.65-3.82c.64-.44,1.28-.9,1.89-1.38a46.35,46.35,0,0,0,12.78-15.09c4-7.55,5.32-15.89,5.38-24.39,0-2.81-.06-5.63-.14-8.45s-.2-5.81-.23-8.72q-.06-4.75-.1-9.51l2,1,5.2,2.69,2.41.41,27.88,4.74,31.12,5.3.94,32,.39,13.29.07,2.25v.33l12.1,4.92Z"
      />
      <path
        fill="#69303a"
        d="M209.67,258.19l-1.54.16a51.49,51.49,0,0,1-40.67-14.65l-27.58-32.51c-.09-2.9,0,0,0-2.91q-.06-4.75-.1-9.51l2,1,5.2,2.69,2.41.41,27.88,4.74,31.12,5.3.94,32Z"
      />
      <path
        fill="#845161"
        d="M138.41,139.06a17.1,17.1,0,0,0-5.64,10.69c-2.09,19.53,5.14,42.58,7.15,62.14l36.42,26.86a51.48,51.48,0,0,0,42.29,8.94c5.87-1.39,9.59-2.29,12.24-7,5.12-9,17.69-39.79,18.51-61.51.23-6.17.08-15.57-3.72-20.82-1.85-2.55-2.8-2.61-2.13-6.21.69-3.76,1.68-7.48,2.51-11.21a52,52,0,0,0,1-16.45c-.55-5-.53-9.44-1.06-14.53-.26-2.47-1.36-13.28-1.36-13.28-6.35,9.56-17.81,14.58-28.41,17.88-14.42,4.48-29.43,6.67-44.05,10.43C162.47,127.52,146.8,131.12,138.41,139.06Z"
      />
      <path
        fill="#00214e"
        d="M181.62,154.13c6.1.05,6.1,9.38,0,9.43h-.27c-6.11-.05-6.11-9.38,0-9.43h.27Z"
      />
      <path
        fill="#00214e"
        d="M234.31,151.28c5.67,0,5.67,8.7,0,8.75h-.25c-5.66-.05-5.66-8.71,0-8.75h.25Z"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M186.09,199.08a29.76,29.76,0,0,0,18.54,9.69"
      />
      <path
        fill="none"
        stroke="#daaea8"
        strokeMiterlimit={10}
        d="M167.42,142.94a80.34,80.34,0,0,1,28.13-.8"
      />
      <path
        fill="none"
        stroke="#daaea8"
        strokeMiterlimit={10}
        d="M226.47,143a55.64,55.64,0,0,1,17.45-1.21"
      />
      <path
        fill="#de8276"
        d="M291.34,294a184.56,184.56,0,0,1-226.48-1l48.66-22.81a46.83,46.83,0,0,0,6.65-3.82c.64-.44,1.28-.9,1.89-1.38,23.55,16.76,55.69,27.33,83.49,14.82,6.62-3,12.7-7.84,16.3-14.06Z"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M198.42,182.25a3.4,3.4,0,0,0,2.11,6.38"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M213.83,153.21c2.49,9.55,4.56,19,6.93,28.57a7,7,0,0,1,.3,2.72c-.43,2.75-3.7,3.88-6.42,4.47"
      />
    </AvatarSvg>
  );
});
AlexAvatar.displayName = 'AlexAvatar';

/**
 * Bella avatar - inline SVG component
 */
export const BellaAvatar = memo((props: AvatarProps) => {
  const { size = 60, className, ref: _ref, ...rest } = props;
  return (
    <AvatarSvg size={size} className={className} {...rest}>
      <path
        fill="#00214e"
        d="M218.31,54.07c-12-4-24.85-7.28-37.54-5.85a115,115,0,0,0-55.55,22C93.33,94,85.13,137,71.81,172.53c-5,13.33-10,26.79-12.17,40.87-6.32,41.77,21.57,56.83,57.72,67.09,45.55,12.93,95.4,9.07,139.81-6.53,37.83-13.29,53-45.33,45-83.6-4-18.88-11.54-36.8-19.14-54.55C268.57,102.13,254.9,66.42,218.31,54.07Z"
      />
      <path
        fill="#e18477"
        d="M307.6,280.15a184.58,184.58,0,0,1-226.48-1l48.66-22.81a47.68,47.68,0,0,0,4.35-2.34l1.12-.7c.4-.25.79-.51,1.18-.78A46.54,46.54,0,0,0,151.1,236c4-7.55,5.32-15.88,5.38-24.38,0-5.73-.31-11.44-.37-17.18q-.06-4.75-.1-9.51l2,1,5.2,2.69,30.29,5.15,31.12,5.3.94,32L226,247l11.47,4.67,9,3.64Z"
      />
      <path
        fill="#a76962"
        d="M225.51,227.76c-2.72,1.68-5.29,2.47-7.54,2.23-14.79-1.59-43.64-13.18-61.8-34.63q0-1.58-.06-3.15-.06-4.76-.1-9.51l2,1,5.2,2.69,30.29,5.15,31.12,5.3Z"
      />
      <path
        fill="#ff2609"
        d="M307.6,280.15a184.58,184.58,0,0,1-226.48-1l48.66-22.81q2.25-1.06,4.35-2.33c23.68,17.41,56.64,28.74,85.06,15.95,8.06-3.62,15.33-10,18.29-18.31l9,3.64Z"
      />
      <circle fill="#e18477" cx="130.67" cy="152.96" r="17" />
      <circle fill="#a76962" cx="137.67" cy="150.96" r="17" />
      <path
        fill="#e18477"
        d="M217.67,217.09c-20.64,3.86-72-16.78-83.74-57.46a98.14,98.14,0,0,1-3.9-26.07c0-1.73,0-3.45.08-5.15,1.28-28.34,15.78-52,38.69-58.65,1.32-.38,2.65-.7,4-1a47.86,47.86,0,0,1,24,1.68,66.87,66.87,0,0,1,26.32,16.67,84.09,84.09,0,0,1,8.52,10,96.4,96.4,0,0,1,14.54,30.05C258,167.9,233.08,214.22,217.67,217.09Z"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M213.32,128.89a58,58,0,0,0,2,11.49c1,3.31,2.52,5.36,4.52,8.22a17.7,17.7,0,0,1,1.79,3c2.65,5.94-.86,12.53-7.54,12.53H210"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M195.34,156.45a3.4,3.4,0,0,0,2.11,6.38"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M218.05,116.61a31.19,31.19,0,0,1,22.85-2.16"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M165.58,119.5a36.76,36.76,0,0,1,31.23-1"
      />
      <path
        fill="#00214e"
        d="M210.21,64.69c-9.92-1.93-21.82-1.59-31.55-1.47-5.31.06-10.69.4-15.75,2-14,4.51-23.11,18-28.79,31.6-1.45,3.47-16.44,38-16.11,38.07,9.6,3.12,19.88-1.22,28.89-5.75,26.74-13.47,55.12-35.12,66.62-63.74C212.45,65.16,211.34,64.91,210.21,64.69Z"
      />
      <path
        fill="#ffffff"
        d="M185,172.55a1.86,1.86,0,0,1,2.68-.5c2.08,1.46,5.88,4.56,11.28,5.64,7.36,1.46,13.75-1.48,15.27.41.86,1.07-.19,2.38-2.2,4.05a19.69,19.69,0,0,1-14.86,3.69c-7.08-1.33-12.4-9.53-12.4-12.43A1.72,1.72,0,0,1,185,172.55Z"
      />
      <path
        fill="#00214e"
        d="M179.41,130.89c6.1.05,6.1,9.37,0,9.42h-.27c-6.1-.05-6.1-9.37,0-9.42h.27Z"
      />
      <path
        fill="#00214e"
        d="M229.67,130.23c5.67,0,5.67,8.7,0,8.74h-.25c-5.67,0-5.67-8.7,0-8.74h.25Z"
      />
    </AvatarSvg>
  );
});
BellaAvatar.displayName = 'BellaAvatar';

/**
 * Chris avatar - inline SVG component (with gradients)
 */
export const ChrisAvatar = memo((props: AvatarProps) => {
  const { size = 60, className, ref: _ref, ...rest } = props;
  const prefix = 'chris';
  return (
    <AvatarSvg size={size} className={className} {...rest}>
      <defs>
        <linearGradient
          id={`${prefix}-linear-gradient`}
          x1="108.28"
          y1="122.42"
          x2="268.26"
          y2="122.42"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#16243f" />
          <stop offset="1" stopColor="#6878b1" />
        </linearGradient>
        <linearGradient
          id={`${prefix}-linear-gradient-2`}
          x1="219.28"
          y1="72.92"
          x2="269.19"
          y2="152.46"
          xlinkHref={`#${prefix}-linear-gradient`}
        />
      </defs>
      <path
        fill="#de8276"
        d="M296.41,282a184.56,184.56,0,0,1-226.48-1l48.66-22.81a46.83,46.83,0,0,0,6.65-3.82c.64-.44,1.28-.9,1.89-1.38a46.35,46.35,0,0,0,12.78-15.09,44.69,44.69,0,0,0,4.64-14.48,67.91,67.91,0,0,0,.74-9.91c0-5.72-.31-11.44-.37-17.17q-.06-4.76-.1-9.51l2,1,5.2,2.69,2.41.41,27.88,4.74,31.12,5.3.94,32,.31,10.46.15,5.08v.33l12.1,4.92Z"
      />
      <path
        fill="#a76962"
        d="M214.63,248.85a16,16,0,0,1-10.07-1.56l-59.67-48.78c-.07-2.26-.13.11-.16-2.15q-.06-4.76-.1-9.51l2,1,5.2,2.69,2.41.41,27.88,4.74,31.12,5.3.94,32,.31,10.46.15,5.08v.33Z"
      />
      <path
        fill={`url(#${prefix}-linear-gradient)`}
        d="M245.43,159.9a35.93,35.93,0,0,1-5.09,4.41c-10.4,7.53-24.28,10-36.14,14.06-5,1.71-59.22,17.12-59.22,20.47,0-.73-5.31-6-12-12.4a79.91,79.91,0,0,1-19.56-85.74c10.91-28.67,45.69-48.43,74.82-53,13.87-2.17,30.33-3.38,43.14,3.27,6.55,3.41,12,8.38,17,13.89q2.34,2.61,4.54,5.33c.63.76,1.25,1.52,1.86,2.29C277.55,101.08,269.48,134.63,245.43,159.9Z"
      />
      <path
        fill={`url(#${prefix}-linear-gradient-2)`}
        d="M245.43,159.9a35.93,35.93,0,0,1-5.09,4.41,22.42,22.42,0,0,1-1.15-2.3c-2.64-6-4-12.51-5-19A275.93,275.93,0,0,1,231,114.49c-.91-15.34-7.46-22.95,5.57-34.91a43,43,0,0,1,16.35-9.38c.63.76,1.25,1.52,1.86,2.29C277.55,101.08,269.48,134.63,245.43,159.9Z"
      />
      <circle fill="#e18477" cx="134.98" cy="158.85" r="17" />
      <circle fill="#a76962" cx="140.38" cy="158.85" r="15.22" />
      <path
        fill="#de8276"
        d="M141.65,142.73c.83,10.86.8,24.12,1.09,28q1.13,14.59,2.24,29.17l66.44,38.81a19.76,19.76,0,0,0,24.68-9.85c9.56-19.58,24.9-50.5,22.88-62-3-17-11-23-11-23l3.33-19.94c3.37-20.27-3.58-39.46-27.26-41.68-5-.46-10.57.49-13.54,4.48-8,10.76-3.39,24-15.4,34C180.53,132.75,148,138,140.38,128.29,140.38,128.29,141.08,135.16,141.65,142.73Z"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M191.51,185.15a29.78,29.78,0,0,0,18.54,9.69"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M215.13,137.94c-.08.35,13.36,36.13,13.36,36.13l-17.94.87"
      />
      <circle fill="#00214e" cx="190.67" cy="150.58" r="15.16" />
      <circle fill="#00214e" cx="242.03" cy="148.13" r="15.16" />
      <line
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        x1="202.93"
        y1="151.47"
        x2="228.46"
        y2="149.29"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M172.65,131a80.58,80.58,0,0,1,28.13-.8"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M231.7,131.13a55,55,0,0,1,17.45-1.21"
      />
      <path
        fill="#00214e"
        d="M296.41,282a184.56,184.56,0,0,1-226.48-1l48.66-22.81a46.83,46.83,0,0,0,6.65-3.82c.64-.44,1.28-.9,1.89-1.38a46.35,46.35,0,0,0,12.78-15.09,44.69,44.69,0,0,0,4.64-14.48,28.66,28.66,0,0,0,2.22,1.94,95.14,95.14,0,0,0,19.82,11.26,99,99,0,0,0,10.46,3.69,93.52,93.52,0,0,0,33,3.49c1.54-.12,3.09-.27,4.63-.38l.15,5.08v.33l12.1,4.92Z"
      />
      <path
        fill="#00214e"
        d="M186.28,142.24c6.1,0,6.1,9.38,0,9.43H186c-6.11,0-6.11-9.38,0-9.43h.27Z"
      />
      <path
        fill="#00214e"
        d="M236.41,140.85c5.66.05,5.66,8.7,0,8.75h-.26c-5.66,0-5.66-8.7,0-8.75h.26Z"
      />
    </AvatarSvg>
  );
});
ChrisAvatar.displayName = 'ChrisAvatar';

/**
 * Daniel avatar - inline SVG component
 */
export const DanielAvatar = memo((props: AvatarProps) => {
  const { size = 60, className, ref: _ref, ...rest } = props;
  return (
    <AvatarSvg size={size} className={className} {...rest}>
      <path
        fill="#00214e"
        d="M122.06,169.38c-.59-1.4-1.16-2.82-1.69-4.25-13-34.59-7.74-76.87,22.21-100.87,8.26-6.62,15.89-12.24,25.74-16.5,3.06-1.32,6.51-2.58,9.65-1.45s4.89,5.87,2.22,7.86a65.82,65.82,0,0,1,20.5-9.67c4.38-1.19,9.2-1.9,13.36-.07s7.06,7.06,5,11.1c6-2.19,12.45-4.07,18.68-2.72,9.39,2,16.72,16,20.75,24a84.12,84.12,0,0,1,8.62,37c.11,10.64-2.82,21.06-6,31.2-4.46,14.21-9.72,28.63-19.62,39.76C231.38,196,220,199.13,205.59,200c-8.4.51-16.78,1.34-25.16,2.08-4.25.38-8.51.76-12.77,1-3.8.23-7.27,2.16-11.1,2.67a15,15,0,0,1-9.55-1.55,22.34,22.34,0,0,1-5.57-5.28,167.81,167.81,0,0,1-11.29-14.4A91.67,91.67,0,0,1,122.06,169.38Z"
      />
      <circle fill="#845161" cx="134.82" cy="161.53" r="17" />
      <circle fill="#69303a" cx="140.68" cy="160.53" r="16" />
      <path
        fill="#845161"
        d="M296.41,284.71a184.56,184.56,0,0,1-226.48-1l48.66-22.81a46.83,46.83,0,0,0,6.65-3.82c.64-.44,1.28-.9,1.89-1.38a46.35,46.35,0,0,0,12.78-15.09c4-7.55,5.32-15.89,5.38-24.39,0-1.74,0-3.49-.06-5.23-.09-4-.27-8-.31-11.94q-.06-4.75-.1-9.51l2,1,5.2,2.69,2.41.41,27.88,4.74,31.12,5.3.94,32,.36,12.21.1,3.33v.33l12.1,4.92Z"
      />
      <path
        fill="#69303a"
        d="M214.74,248.08l-1.54.16a51.49,51.49,0,0,1-40.67-14.65l-27.38-31.91c-.09-2.9-.2-.6-.23-3.51q-.06-4.75-.1-9.51l2,1,5.2,2.69,2.41.41,27.88,4.74,31.12,5.3.94,32Z"
      />
      <path
        fill="#845161"
        d="M155.85,128.81a80.43,80.43,0,0,0-1,16c-.9-.19-17.43-4.6-17.31-3.22.87,10.36,6.38,50.77,7.43,61l36.42,26.86a51.47,51.47,0,0,0,42.29,8.94c5.87-1.38,9.59-2.29,12.24-7,5.12-9,17.69-39.79,18.51-61.51.23-6.17.08-15.57-3.72-20.82-1.85-2.55-2.8-2.61-2.13-6.21.69-3.76,1.68-7.47,2.51-11.21,2-9.06,2.75-17.46.75-26.74-1.37-6.33-7.47-17.72-7.47-17.72-.81,4.61-8,11.55-14.87,12.94-15.83,3.2-28.73-3.86-44-4.63-6.43-.33-13.21,1.18-18,5.46-3.68,3.27-5.91,7.86-7.63,12.48A80.56,80.56,0,0,0,155.85,128.81Z"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M220.19,144.81a56.66,56.66,0,0,0,2,11.49c1,3.31,2.51,5.36,4.52,8.21a19.71,19.71,0,0,1,1.79,3c2.64,5.94-.87,12.53-7.55,12.53h-4.13"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M191.16,189.84a29.76,29.76,0,0,0,18.54,9.69"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M172.49,133.7a80.34,80.34,0,0,1,28.13-.8"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M231.54,133.81A55.35,55.35,0,0,1,249,132.59"
      />
      <path
        fill="#ff2609"
        d="M296.41,284.71a184.56,184.56,0,0,1-226.48-1l48.66-22.81a46.83,46.83,0,0,0,6.65-3.82c.64-.44,1.28-.9,1.89-1.38,23.55,16.76,55.69,27.33,83.49,14.82,6.62-3,12.7-7.84,16.3-14.06Z"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M203.49,173a3.4,3.4,0,0,0,2.11,6.38"
      />
      <path fill="#00214e" d="M188,141.33c6.1,0,6.1,9.38,0,9.43h-.28c-6.1,0-6.1-9.38,0-9.43H188Z" />
      <path
        fill="#00214e"
        d="M238.25,141.33c5.66,0,5.66,8.71,0,8.75H238c-5.66,0-5.66-8.7,0-8.75h.26Z"
      />
    </AvatarSvg>
  );
});
DanielAvatar.displayName = 'DanielAvatar';

/**
 * Emma avatar - inline SVG component (with gradient)
 */
export const EmmaAvatar = memo((props: AvatarProps) => {
  const { size = 60, className, ref: _ref, ...rest } = props;
  const prefix = 'emma';
  return (
    <AvatarSvg size={size} className={className} {...rest}>
      <defs>
        <linearGradient
          id={`${prefix}-linear-gradient`}
          x1="69.93"
          y1="286.4"
          x2="296.41"
          y2="286.4"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#16243f" />
          <stop offset="1" stopColor="#6878b1" />
        </linearGradient>
      </defs>
      <path
        fill="#f2d4cf"
        d="M270.44,208.43S85.12,200,83.18,199.92c-5-6.76.42-22.1,4.31-28.06,6.74-10.32,18.92-17,22.94-28.66,4.63-13.45-3.12-24.06-4.33-36.78C105,94.35,109.87,82,117.85,73,134,55,160.79,46.08,186.66,46.28c13.5.09,26.74,2.67,38.08,7.7,1.38.61,2.72,1.25,4,1.93,1,.53,2,1.09,3,1.68a75.15,75.15,0,0,1,26.95,27.59c7.91,13.84,9.79,26.67,5.16,42-3,9.82-6.73,20-4.87,30,2,11.05,10.54,20,12.78,31C273.19,194.94,272.14,201.86,270.44,208.43Z"
      />
      <path
        fill="#f2a196"
        d="M296.41,281.75a184.56,184.56,0,0,1-226.48-1l48.66-22.81a46.83,46.83,0,0,0,6.65-3.82c.64-.44,1.28-.9,1.89-1.38a46.35,46.35,0,0,0,12.78-15.09,44.69,44.69,0,0,0,4.64-14.48,67.91,67.91,0,0,0,.74-9.91c0-5.72-.31-11.44-.37-17.17q-.06-4.76-.1-9.51l2,1,5.2,2.69,2.41.41,27.88,4.74,31.12,5.3.94,32,.31,10.46.15,5.08v.33l12.1,4.92Z"
      />
      <path
        fill="#e88870"
        d="M214.71,246.19a16,16,0,0,1-10.07-1.56L145,195.85c-.07-2.26-.13.11-.16-2.15q-.06-4.75-.1-9.51l2,1,5.2,2.69,2.41.41,27.88,4.74,31.12,5.3.94,32,.31,10.46.15,5.08v.33Z"
      />
      <path
        fill={`url(#${prefix}-linear-gradient)`}
        d="M296.41,281.75a184.56,184.56,0,0,1-226.48-1l48.66-22.81a46.83,46.83,0,0,0,6.65-3.82c.64-.44,1.28-.9,1.89-1.38,23.55,16.76,55.69,27.33,83.49,14.82,6.62-3,12.7-7.84,16.3-14.06Z"
      />
      <circle fill="#f2a196" cx="132.97" cy="160.54" r="17" />
      <circle fill="#e88870" cx="138.36" cy="160.29" r="15.22" />
      <path
        fill="#f2a196"
        d="M137.83,144.25,144.89,201c.18,1.46,11,5.74,12.27,6.41C171.66,215,186.59,225.1,203,227.7c7,1.11,14.35,1,21-1.57,11.72-4.5,13.3-13.32,17.32-23.7a184.21,184.21,0,0,0,8.52-28.81c2-9.4,3.78-22.33-3.12-30.39-.84-1-1.74-1.53-1.57-3,1.46-12.32,3.78-26.26-1.59-37.9-3.5-7.57-9.16-13.94-15.37-19.49,0,0-47.23,35.5-51.52,38C166.44,126.71,137.83,144.25,137.83,144.25Z"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M215.17,141c0,.29,12.66,29.82,12.66,29.82l-15,1.45"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M169.14,128.61a80.61,80.61,0,0,1,28.06-2.11"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M228.28,128.3a55.39,55.39,0,0,1,17.38-2"
      />
      <path
        fill="#ffffff"
        d="M191.25,182.07a1.86,1.86,0,0,1,2.66-.62c2.14,1.37,6.08,4.3,11.52,5.13,7.41,1.14,13.67-2.08,15.27-.25.91,1-.08,2.38-2,4.14A19.72,19.72,0,0,1,204,194.81c-7.13-1-12.81-9-12.93-11.88A1.7,1.7,0,0,1,191.25,182.07Z"
      />
      <path
        fill="#00214e"
        d="M186.58,139.76c6.09-.27,6.58,9.05.49,9.41h-.27c-6.1.27-6.59-9.05-.49-9.41h.27Z"
      />
      <path
        fill="#00214e"
        d="M236.74,136.48c5.65-.25,6.11,8.39.45,8.73h-.25c-5.66.25-6.11-8.39-.46-8.73h.26Z"
      />
    </AvatarSvg>
  );
});
EmmaAvatar.displayName = 'EmmaAvatar';

/**
 * Frank avatar - inline SVG component (with gradient)
 */
export const FrankAvatar = memo((props: AvatarProps) => {
  const { size = 60, className, ref: _ref, ...rest } = props;
  const prefix = 'frank';
  return (
    <AvatarSvg size={size} className={className} {...rest}>
      <defs>
        <linearGradient
          id={`${prefix}-linear-gradient`}
          x1="69.93"
          y1="299.08"
          x2="296.41"
          y2="299.08"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#315676" />
          <stop offset="1" stopColor="#e1b9e0" />
        </linearGradient>
      </defs>
      <path
        fill="#00214e"
        d="M278.48,125.53c0,7.87-8.9,14.47-10.84,21.73-2,7.52,2.34,17.67-1.46,24.25s-14.87,8-20.28,13.37-6.71,16.42-13.38,20.28-16.73-.54-24.24,1.47c-7.26,1.94-13.86,10.84-21.74,10.84s-14.47-8.9-21.73-10.84c-7.51-2-17.67,2.34-24.24-1.47s-8-14.86-13.38-20.28-16.42-6.7-20.28-13.37.54-16.73-1.46-24.25c-1.94-7.26-10.84-13.86-10.84-21.73s8.9-14.47,10.84-21.73c2-7.52-2.34-17.67,1.46-24.25s14.87-8,20.28-13.37,6.7-16.42,13.38-20.28,16.73.54,24.24-1.47c7.26-1.94,13.86-10.84,21.73-10.84s14.48,8.9,21.74,10.84c7.51,2,17.66-2.34,24.24,1.47s8,14.86,13.38,20.28,16.42,6.7,20.28,13.37-.55,16.73,1.46,24.25C269.58,111.06,278.48,117.66,278.48,125.53Z"
      />
      <circle fill="#e18477" cx="134.82" cy="171.25" r="17" />
      <circle fill="#a76962" cx="140.68" cy="170.25" r="16" />
      <path
        fill="#e18477"
        d="M296.41,294.43a184.56,184.56,0,0,1-226.48-1l48.66-22.81a46.83,46.83,0,0,0,6.65-3.82c.64-.44,1.28-.9,1.89-1.38a46.35,46.35,0,0,0,12.78-15.09c4-7.55,5.32-15.89,5.38-24.39,0-5.72-.31-11.44-.37-17.17q-.06-4.75-.1-9.51l2,1L152,203l2.41.41,27.88,4.74,31.12,5.3.94,32,.46,15.54v.33l12.1,4.92Z"
      />
      <path
        fill="#a76962"
        d="M214.69,257.38l-1.54.16a51.49,51.49,0,0,1-40.67-14.65L145.1,210.38c-.09-2.9-.2,0-.23-2.91q-.06-4.75-.1-9.51l2,1,5.2,2.69,2.41.41,27.88,4.74,31.12,5.3.94,32Z"
      />
      <path
        fill="#e18477"
        d="M139.93,136.65c-.79,1-1.49,2.95-2.24,6.11-1.73,7.33,5.85,55.46,7.3,69.62l36.42,26.86a51.51,51.51,0,0,0,42.29,8.94c5.87-1.39,9.59-2.29,12.24-7,5.12-9,17.69-39.79,18.51-61.51.23-6.16.08-15.56-3.72-20.82-1.85-2.55-2.8-2.6-2.13-6.21s1.68-7.47,2.51-11.21c2.29-10.33,4.12-22,.81-32.28-1.76-5.5-4.87-9.14-9.29-12.67-4.72-3.78-6.67-3.6-9.5,2-1.91,3.8-3.83,7-7.7,9.16-5.61,3.11-9.43,1.09-15,.66-4.88-.37-4.36,3.4-5.43,7a16.87,16.87,0,0,1-6.77,9.25,18.53,18.53,0,0,1-20,.11,4.75,4.75,0,0,0-2.85-.89c-3.34.18-3.49,4.19-4.25,7.07-1.61,6-8.62,9.32-14.85,8.84C146.12,138.92,142.5,133.26,139.93,136.65Z"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M220.19,154.54a57.27,57.27,0,0,0,2,11.48c1,3.31,2.52,5.37,4.52,8.22a18.39,18.39,0,0,1,1.79,3c2.64,5.94-.87,12.53-7.55,12.53h-4.13"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M191.16,199.57a29.83,29.83,0,0,0,18.54,9.69"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M172.49,143.42a80.57,80.57,0,0,1,28.13-.79"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M231.54,143.53A55,55,0,0,1,249,142.32"
      />
      <path
        fill={`url(#${prefix}-linear-gradient)`}
        d="M296.41,294.43a184.56,184.56,0,0,1-226.48-1l48.66-22.81a46.83,46.83,0,0,0,6.65-3.82c.64-.44,1.28-.9,1.89-1.38,23.55,16.76,55.69,27.33,83.49,14.82,6.62-3,12.7-7.84,16.3-14.06Z"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M203.49,182.73a3.41,3.41,0,0,0,2.11,6.39"
      />
      <path
        fill="#00214e"
        d="M187.17,152.93c6.11,0,6.11,9.38,0,9.43h-.27c-6.1-.05-6.1-9.38,0-9.43h.27Z"
      />
      <path
        fill="#00214e"
        d="M237.44,152.27c5.66,0,5.66,8.7,0,8.75h-.26c-5.66-.05-5.66-8.7,0-8.75h.26Z"
      />
    </AvatarSvg>
  );
});
FrankAvatar.displayName = 'FrankAvatar';

/**
 * Grace avatar - inline SVG component (multiple gradients)
 */
export const GraceAvatar = memo((props: AvatarProps) => {
  const { size = 60, className, ref: _ref, ...rest } = props;
  const prefix = 'grace';
  return (
    <AvatarSvg size={size} className={className} {...rest}>
      <defs>
        <linearGradient
          id={`${prefix}-linear-gradient`}
          x1="-1177.82"
          y1="131.56"
          x2="-1082.82"
          y2="131.56"
          gradientTransform="translate(1276.99 198.93) rotate(13.12)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#16243f" />
          <stop offset="1" stopColor="#6878b1" />
        </linearGradient>
        <linearGradient
          id={`${prefix}-linear-gradient-2`}
          x1="98.71"
          y1="168.67"
          x2="260.69"
          y2="168.67"
          xlinkHref={`#${prefix}-linear-gradient`}
        />
        <linearGradient
          id={`${prefix}-linear-gradient-3`}
          x1="58.45"
          y1="339.99"
          x2="265.95"
          y2="282.46"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#315676" />
          <stop offset="1" stopColor="#e1b9e0" />
        </linearGradient>
        <linearGradient
          id={`${prefix}-linear-gradient-4`}
          x1="111.94"
          y1="98.26"
          x2="210.94"
          y2="139.54"
          xlinkHref={`#${prefix}-linear-gradient`}
        />
      </defs>
      <path
        fill={`url(#${prefix}-linear-gradient)`}
        d="M192.58,81.19c-1.37,5.88-9.2,8.78-12.51,13.39-3.43,4.79-3.74,13.1-8.66,16.16s-12.52-.34-18.32.62c-5.61.93-11.68,6.68-17.55,5.31s-8.78-9.21-13.39-12.52c-4.79-3.43-13.1-3.74-16.16-8.66s.34-12.51-.62-18.32c-.93-5.61-6.68-11.67-5.31-17.55s9.21-8.78,12.52-13.39c3.43-4.79,3.74-13.1,8.66-16.16s12.51.34,18.32-.62c5.61-.93,11.67-6.67,17.55-5.3s8.78,9.2,13.39,12.51c4.79,3.43,13.1,3.74,16.16,8.66s-.34,12.52.62,18.33C188.21,69.25,194,75.32,192.58,81.19Z"
      />
      <path
        fill={`url(#${prefix}-linear-gradient-2)`}
        d="M210.4,216c-2.52,3-4.7,6.28-7.26,9.23-13.62,15.66-43.3,5.37-58.06-4-10.83-6.85-19-17.18-26-27.94-8.6-13.27-15.79-27.7-18.9-43.2C85.93,79.1,177,48.22,228.07,83.07c19,13,36.18,42.16,32,65.53-2.13,11.86-10.57,22.53-9.67,34.54,1,12.94,12.7,25.27,7.8,37.29-1.92,4.7-6.05,8.06-9.41,11.87-4.53,5.15-7.83,11.59-8.23,18.43s2.45,14,8.1,17.92c-13.93-1.86-22.52-18.54-18.91-32.12,1.18-4.46,3.35-8.62,4.3-13.14s.4-9.77-3.05-12.83c-4.43-3.93-11.65-2.44-16.31,1.22A30.41,30.41,0,0,0,210.4,216Z"
      />
      <path
        fill="#845161"
        d="M296.41,304.08a184.56,184.56,0,0,1-226.48-1l48.66-22.81a47.68,47.68,0,0,0,4.35-2.34l1.12-.7c.4-.25.79-.51,1.18-.78A46.54,46.54,0,0,0,139.91,260c4-7.55,5.32-15.89,5.38-24.39,0-5.72-.31-11.44-.37-17.17q-.06-4.75-.1-9.51l.2.1,1.78.92,5.2,2.69,30.29,5.15,31.12,5.3.72,24.47.22,7.5.47,15.87,11.47,4.67,9,3.64Z"
      />
      <path
        fill="#69303a"
        d="M214.13,247.53a12,12,0,0,1-7.22,2c-14.79-1.59-43.64-13.18-61.8-34.63q0-1.58-.06-3.15l0-2.79,1.78.92,5.2,2.69,30.29,5.15,31.12,5.3Z"
      />
      <path
        fill={`url(#${prefix}-linear-gradient-3)`}
        d="M296.41,304.09a184.56,184.56,0,0,1-226.48-1l48.66-22.81a49.22,49.22,0,0,0,4.36-2.34c23.67,17.41,56.63,28.75,85.05,16,8.06-3.62,15.33-10.05,18.29-18.31l9,3.64Z"
      />
      <circle fill="#845161" cx="119.82" cy="176.91" r="17" />
      <circle fill="#69303a" cx="125.82" cy="174.91" r="17" />
      <path
        fill="#845161"
        d="M235.36,151.16C247.1,191.85,222.16,241,206.82,241c-21,0-72-16.77-83.73-57.46S127,102.65,158,93.7,223.62,110.48,235.36,151.16Z"
      />
      <ellipse
        fill="none"
        stroke="#ffffff"
        strokeMiterlimit={10}
        cx="170.38"
        cy="164.41"
        rx="19"
        ry="11.84"
      />
      <ellipse
        fill="none"
        stroke="#ffffff"
        strokeMiterlimit={10}
        cx="225.18"
        cy="161.85"
        rx="19"
        ry="11.84"
      />
      <path
        fill={`url(#${prefix}-linear-gradient-4)`}
        d="M200.54,101.22c-2.4,4.74-7.4,8.86-10.37,13.71-3.89,6.35-6.83,13.25-10.13,19.92-2.45,5-5.18,9.94-9.33,13.61s-10,5.84-15.31,4.31c-3.64-1.05-6.59-3.65-9.85-5.58s-7.43-3.22-10.77-1.42c-4.92,2.64-4.85,9.81-8.37,14.14-8.07,9.93-22.88-3.45-24.67-12.12-2.6-12.67,7.79-23.26,15.35-32,6.31-7.3,14.83-18.44,24.73-20.87.18,0,58-23,58-23,.52,7.73,3.64,19.16,1.78,26.56A13,13,0,0,1,200.54,101.22Z"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M198.4,153.06c-.05.24,5.54,15.88,8.93,25.32a5.26,5.26,0,0,1-4.94,7h-3.64"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M209.86,142.85a31.18,31.18,0,0,1,22.85-2.16"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M154.75,146.55a36.72,36.72,0,0,1,31.23-1"
      />
      <line
        fill="none"
        stroke="#ffffff"
        strokeMiterlimit={10}
        x1="189.38"
        y1="164.41"
        x2="206.32"
        y2="163.11"
      />
      <path
        fill="#00214e"
        d="M172.92,157.63c6.1,0,6.1,9.37,0,9.42h-.27c-6.11-.05-6.11-9.38,0-9.42h.27Z"
      />
      <path
        fill="#00214e"
        d="M223.18,154.54c5.66.05,5.66,8.7,0,8.75h-.25c-5.67,0-5.67-8.7,0-8.75h.25Z"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M191.3,179.65a3.38,3.38,0,0,0-3,3.15,2.65,2.65,0,0,0,3.24,2.5"
      />
      <path
        fill="#ffffff"
        d="M173.65,194.77a1.86,1.86,0,0,1,2.68-.5c2.08,1.46,5.88,4.56,11.28,5.63,7.36,1.47,13.75-1.48,15.27.42.86,1.07-.19,2.37-2.2,4.05a19.74,19.74,0,0,1-14.86,3.69c-7.08-1.33-12.4-9.53-12.4-12.44A1.68,1.68,0,0,1,173.65,194.77Z"
      />
    </AvatarSvg>
  );
});
GraceAvatar.displayName = 'GraceAvatar';

/**
 * Henry avatar - inline SVG component (with gradient)
 */
export const HenryAvatar = memo((props: AvatarProps) => {
  const { size = 60, className, ref: _ref, ...rest } = props;
  const prefix = 'henry';
  return (
    <AvatarSvg size={size} className={className} {...rest}>
      <defs>
        <linearGradient
          id={`${prefix}-linear-gradient`}
          x1="109.87"
          y1="140.89"
          x2="166.58"
          y2="65.41"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.29" stopColor="#00214e" />
          <stop offset="0.51" stopColor="#6878b1" />
          <stop offset="0.79" stopColor="#00214e" />
        </linearGradient>
      </defs>
      <path
        fill="#00214e"
        d="M258.38,149.67c-.41-33.19-6.33-69.59-36.31-89.15C195,42.86,158.32,43.72,131.45,61a77.55,77.55,0,0,0-10.22,7.8,105.09,105.09,0,0,0-26.66,39,130.84,130.84,0,0,0-9,54.2c1.67,37.14-2.36,74.39-2.36,111.68,0,1.29,0,2.77,1,3.59a4.27,4.27,0,0,0,3,.59c15.62-.87,29.45-3.57,44.53-6.08,18.8-3.12,39.07-2.23,58.1-3.42,11.33-.66,23.24-2.49,34.57-2,3.61.16,35.22,4.85,35.36,6.8C256.75,231.8,258.89,191,258.38,149.67ZM148,145.49l-14.09-10a130.31,130.31,0,0,0,31.94-16,70.63,70.63,0,0,0,21.84-23l5.71,3.61Z"
      />
      <path
        fill="#f2a196"
        d="M296.41,280.37a184.56,184.56,0,0,1-226.48-1l48.66-22.81a47.68,47.68,0,0,0,4.35-2.34l1.12-.7c.4-.25.79-.51,1.18-.78a46.54,46.54,0,0,0,14.67-16.47c4-7.55,5.32-15.89,5.38-24.39,0-4.67-.19-9.34-.31-14q0-1.57-.06-3.15-.06-4.75-.1-9.51l.07,0,1.91,1,5.2,2.69,30.29,5.15,31.12,5.3.71,24,.2,6.88,0,1.07.47,15.87,11.47,4.67,9,3.64Z"
      />
      <path
        fill="#e88870"
        d="M214.12,223.37a12.12,12.12,0,0,1-7.34,2.11C192,223.89,163.14,212.3,145,190.85q0-1.57-.06-3.15l0-2.47,1.91,1,5.2,2.69,30.29,5.15,31.12,5.3Z"
      />
      <circle fill="#f2a196" cx="119.82" cy="153.19" r="17" />
      <circle fill="#e88870" cx="125.82" cy="151.19" r="17" />
      <path
        fill="#f2a196"
        d="M235.36,127.45c11.74,40.68-13.2,89.87-28.54,89.87-21,0-72-16.78-83.73-57.46S127,78.94,158,70,223.62,86.76,235.36,127.45Z"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M177.31,176.87a29.74,29.74,0,0,0,18.54,9.69"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M197.93,131.73c-.05.2,4.79,8.56,7.31,17.59a58,58,0,0,1,1.62,14.76H193.35"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M210.34,123.22a31.18,31.18,0,0,1,22.85-2.16"
      />
      <path
        fill="none"
        stroke="#00214e"
        strokeMiterlimit={10}
        d="M151.19,127.2a36.75,36.75,0,0,1,31.23-1"
      />
      <path
        fill="#00214e"
        d="M168.73,136.06c6.1.05,6.1,9.38,0,9.42h-.27c-6.1,0-6.1-9.37,0-9.42h.27Z"
      />
      <path
        fill="#00214e"
        d="M218.86,134.67c5.66,0,5.66,8.7,0,8.74h-.25c-5.67,0-5.67-8.7,0-8.74h.25Z"
      />
      <path
        fill="#00214e"
        d="M198.93,59.32a113.91,113.91,0,0,1-2.27,14,81,81,0,0,1-9,23.18,70.63,70.63,0,0,1-21.84,23,130.31,130.31,0,0,1-31.94,16,202.94,202.94,0,0,1-27.46,7.23c.31-7.63,0-17.07.94-25.93.7-6.36,2.1-12.43,4.92-17.32a79.54,79.54,0,0,1,32.19-30.3l.12-.06C159.38,61.51,182.29,54,198.93,59.32Z"
      />
      <path
        fill="#f2d4cf"
        d="M296.41,280.37a184.56,184.56,0,0,1-226.48-1l48.66-22.81a46.83,46.83,0,0,0,6.65-3.82c.64-.44,1.28-.9,1.89-1.38a46.35,46.35,0,0,0,12.78-15.09,44.69,44.69,0,0,0,4.64-14.48,28.66,28.66,0,0,0,2.22,1.94A95.14,95.14,0,0,0,166.59,235a99,99,0,0,0,10.46,3.69,93.52,93.52,0,0,0,33,3.49c1.54-.12,3.09-.27,4.63-.38l.15,5.08v.33l12.1,4.92Z"
      />
      <path
        fill={`url(#${prefix}-linear-gradient)`}
        d="M187.64,96.54a70.63,70.63,0,0,1-21.84,23,130.31,130.31,0,0,1-31.94,16l-26.52-18.7-12.77-9a105.09,105.09,0,0,1,26.66-39A77.55,77.55,0,0,1,131.45,61l13,8.22Z"
      />
    </AvatarSvg>
  );
});
HenryAvatar.displayName = 'HenryAvatar';
